/** Representa un Bill of Lading (BL) con datos de operación, tonelaje y estatus de abandono. */
export interface Bl {
  /** Número de operación. */
  operation_number:      number;
  /** Nombre del barco. */
  boats_name:            string;
  /** Número del tipo de operación. */
  operation_type_number: OperationType;
  /** Tonelaje del BL. */
  bl_tonnage:            number;
  /** Tonelaje restante. */
  remaining_tonnage:     number;
  /** Fecha de inicio de operaciones (puede ser nula). */
  start_operations:      string | null;
  /** Fecha de fin de operaciones (puede ser nula). */
  end_operations:        string | null;
  /** Identificador de la operación. */
  id_operation:          number;
  /** Descripción del tipo de operación. */
  operation_type:        string;
  /** Estatus de abandono. */
  abandonment_status:    number;
  /** Días en abandono. */
  days_abandonment:      number;
  /** Indica si el BL está activo. */
  active:                boolean;
  /** Texto combinado número-nombre del barco. */
  no_name_ship?:         string;
  /** Tonelaje de salida. */
  output_tonnage?:       number;
}

/** Tipos de operación aduanal posibles para un BL. */
export enum OperationType {
  /** Operación general. */
  General = 0,
  /** Importación. */
  Importation = 1,
  /** Exportación. */
  Exportation,
  /** Cabotaje de exportación. */
  CabotageExp,
  /** Cabotaje de importación. */
  CabotageImp,
}

/** Nombres posibles de muelle. */
export type DockName = "Muelle 1" | "Muelle 2";

/** Detalle ampliado de un BL con datos de atraque, calado y autoridad. */
export interface BlDetails {
  /** Número de operación. */
  operation_number:             number;
  /** Nombre del barco. */
  boats_name:                   string;
  /** Número del tipo de operación. */
  operation_type_number:        OperationType;
  /** Tonelaje del BL. */
  bl_tonnage:                   number;
  /** Nombre del cliente (puede ser nulo). */
  customer_name:                null | string;
  /** Fecha y hora de captura (puede ser nula). */
  date_time_capture:            null | string;
  /** Nombre del muelle. */
  dock_name:                    string;
  /** Fecha y hora estimada de arribo. */
  EstimatedDateTimeArrival?:     string;
  /** Fecha de atraque (puede ser nula). */
  BerthDate?:                    string | null;
  /** Inicio del segundo cabo de atraque. */
  startSecondCapeBerth?:         string;
  /** Autoridad de salida. */
  outputAuthority?:            string;
  /** Fin del calado inicial. */
  FinishDraftInitial?:           string;
  /** Nombre del puerto de origen. */
  port_origin_name:             string;
  /** Inicio del calado inicial. */
  startDraftInitial?:             string;
  /** Fin del calado inicial. */
  finishDraftInitial?:            string;
  /** Fecha de inicio de operaciones (puede ser nula). */
  start_operations:             null | string;
  /** Fecha de fin de operaciones (puede ser nula). */
  end_operations:               null | string;
  /** Aviso de alistamiento (puede ser nulo). */
  notice_of_readiness:          null | string;
  /** Nacionalidad del barco. */
  boat_nationality:             string;
  /** Puerto de destino. */
  destination_port:             string;
  /** Identificador de la operación. */
  operation_id:                 number;
  /** Fecha de atraque. */
  docking_date:                 string;
  /** Fecha de desatraque (puede ser nula). */
  undocking_date:               null | string;
  /** BL máster (puede ser nulo). */
  bl_master:                    null | string;
  /** Tonelaje restante. */
  remaining_tonnage?:           number;
  /** Tonelaje total de salida. */
  output_total_tonnage?:        number;
  /** Estatus de abandono. */
  abandonment_status?:          number;
  /** Descripción del tipo de operación. */
  operation_type?:              string;
  /** Identificador en SAP. */
  id_sap?:                       string;
  /** Fecha de desatraque (puede ser nula). */
  DateUndocking?:                null | string;
  /** Inicio del calado final (puede ser nulo). */
  startDraftFinal?:              string | null;
  /** Fin del calado final (puede ser nulo). */
  endDraftFinal?:                string | null;
  /** Inicio del primer cabo de desatraque (puede ser nulo). */
  startFirstCapeUndocking?:      string | null;
  /** Horas de preparación de atraque. */
  HoursPrepDocking?:             number;
  /** Horas de preparación de desatraque. */
  HoursPrepUndocking?:           number;
  /** Fecha mínima de atraque (puede ser nula). */
  MinimumDockingDate?:           string | null;
  /** Fecha contable (puede ser nula). */
  AccountingDate?:               string | null;
  /** Fecha y hora estimada de partida (puede ser nula). */
  DateTimeEstimatedDeparture?:   string | null;
  /** Autoridad de arribo (puede ser nula). */
  arrivalAuthority?:             string | null;
  /** Indica estatus en cero. */
  status_zero?:                  boolean;
  /** Indica si hay rectificación. */
  rectification?:                boolean;
}

/** BL máster identificado por su número. */
export interface BlMaster {
  /** Número del BL máster. */
  no_bl_master: string;
}

/** Datos para crear un BL máster. */
export interface BlMasterCreate {
  /** Número del BL máster. */
  no_bl_master: string;
  /** Número del barco asociado. */
  boat_number:        number;
  /** Usuario que realiza la actualización. */
  user_update:  string;
}

/** Respuesta con el número del BL máster. */
export interface BlMasterResponse {
  /** Número del BL máster. */
  no_bl_master: string;
}

// export interface SalidaUpdate {
//   operation_number:  number;
//   boats_name:        string;
//   bl_id:             number;
//   bl_tonnage:        number;
//   customer_name:     string;
//   date_time_capture: Date;
//   material_name:     string;
//   dock_name:         string;
//   port_origin_name:  null;
//   start_operations:  Date;
//   end_operations:    Date;
//   boat_nationality:  string;
//   destination_port:  string;
//   id_operacion:      number;
// }

/** Cliente asociado a un BL, con datos de material y tonelaje. */
export interface ClientesBl {
  /** Número del barco. */
  boat_number:     number;
  /** Identificador de la operación. */
  operation_id:    number;
  /** Identificador del material. */
  material_id:     number;
  /** Identificador del grupo de material. */
  material_group_id:number;
  /** Nombre del cliente. */
  customer_name:   string;
  /** Nombre del material. */
  material_name:   string;
  /** Presentación del material (puede ser nula). */
  presentation:    null | string;
  /** Cantidad de piezas. */
  quantity_pieces: number;
  /** Tonelaje del BL. */
  tonnage_bl:      number;
  /** Identificador del registro. */
  id:              number;
  /** Identificador de la presentación. */
  id_presentation: number;
  /** Identificador del cliente. */
  customer_id:     number;
  /** Identificador del BL. */
  bl_id:           number;
}

/** Archivo asociado a un BL/barco para su carga. */
export interface FileBl {
  /** Número del barco. */
  boat_number: number;
  /** Clave/ruta del archivo. */
  file:  string;
}

/** Referencia a la URL de un archivo de BL guardado. */
export interface FileBlSaved {
  /** URL para acceder al archivo. */
  url: string;
}

/** Parámetro para obtener el archivo de un BL por número de barco. */
export interface GetFileBl {
  /** Número del barco. */
  boat_number: number;
}

/** Configuración de una tabla dinámica: encabezados, estilos, campos e iconos. */
export interface TableConfig {
  /** Encabezados de la tabla como pares [etiqueta, campo]. */
  tableHeads: [string, string][];
  /** Encabezados del contenedor de la tabla. */
  tableHeadsContainer: string[];
  /** Estilos CSS aplicados a la tabla y sus filas. */
  styles: {
    /** Estilos de los encabezados de la tabla. */
    table_heads: {
      'grid-template-columns':  string;
      'min-width'?: string
      'font-size'?:             string;
      'margin'?:                string;
      'padding'?:               string;
    },
    /** Estilos de las filas de la tabla. */
    table_row: {
      'grid-template-columns': string;
      'min-width'?: string
    },
    /** Estilos de la fila de totales (opcional). */
    table_row_totals?: {
      'background-color': string;
      'grid-template-columns': string;
      'color': string;
      'min-width'?: string
    },
    /** Estilos del contenedor de iconos. */
    container_icons: {
      'grid-template-columns': string;
    }
  },
  /** Campos a renderizar en cada fila. */
  arrayFields: string[];
  /** Iconos de acción por fila. */
  iconos: [string, string, string, string?][];
  /** Mensaje a mostrar cuando no hay datos. */
  mensaje: string;
  /** Indica si la tabla permite filtrado. */
  filter: boolean;
  /** Campo por el cual agrupar las filas. */
  groupByField?: string;
  /** Campo a totalizar. */
  totalField?: string;
  /** Campo de total de piezas. */
  totalPiecesField?: string;
  /** Campo de subtotal. */
  subtotalField?: string;
  /** Configuración y valores de los totales. */
  totals?: {
    show: boolean;
    content: string[];
    total: number;
    total_output?: number;
    total_remaining?: number;
    total_percent?: number;
    piezas?: number;
    totalOriginWeight?: number;
    totalGrossWeight?: number;
    totalTareWeight?: number;
    totalNetWeight?: number;
  }
}

/** Barco de exportación con número, nombre y fecha. */
export interface ExportBoats {
  /** Número del barco. */
  boats_number:           number;
  /** Nombre del barco. */
  boats_name:             string;
  /** Fecha asociada (ISO 8601). */
  date:                   string;
}

/** Barco de cabotaje identificado por su número. */
export interface CabotageBoat {
  /** Número del barco. */
  boat_number:          number;
}

/** Solicitud de balance de un barco. */
export interface ShipBalance {
  /** Número del barco. */
  ship_no:          number;
  /** Estatus del balance. */
  status:           boolean;
}

/** Respuesta de la operación de balance de un barco. */
export interface ShipBalanceResponse {
  /** Indica si la operación produjo error. */
  error:            boolean;
  /** Mensaje descriptivo del resultado. */
  message:          string;
}
